import classes from './ContentSystemArticlesPage.module.css';
import { PageHeadingBanner } from '../../components/PageHeadingBanner/PageHeadingBanner';
import { TabMenu } from '../../components/TabMenu/TabMenu';
import { useEffect, useState } from 'react';
import { Alert } from '../../components/Alert/Alert';
import { Paragraph } from '../../components/Paragraph';
import { ArticleResponse, Label, Userinfo, UserRoles } from '../../api/queries';
import { useGetArticlesQuery } from '../../hooks/queries/useGetArticlesQuery/useGetArticlesQuery';
import { Heading } from '../../components/Heading';
import { ArticlesList } from '../../components/ArticlesList/ArticlesList';
import { useGetDraftArticlesQuery } from '../../hooks/queries/useGetDraftArticlesQuery';
import { Pagination } from '../../components/Pagination/Pagination';
import { useGetUserinfo } from '../../hooks/queries/useGetUserinfo';

type BaseTabs = 'Min HR-avdeling' | 'Trustpulse';

export type ArticleTabs<IsCreator extends boolean> = IsCreator extends true
  ? BaseTabs | 'Utkast'
  : BaseTabs;

const pageTabs = <IsCreator extends boolean>(
  isContentCreator: IsCreator,
): ArticleTabs<IsCreator>[] => [
  'Min HR-avdeling',
  'Trustpulse',
  ...(isContentCreator ? (['Utkast'] as ArticleTabs<IsCreator>[]) : []),
];

export const ContentSystemArticlesPage = () => {
  const [selectedLabels, setSelectedLabels] = useState<Array<Label>>([]);
  const [sortBy, setSortBy] = useState<'asc' | 'desc'>('desc');
  const [tab, setTab] = useState<ArticleTabs<boolean>>(pageTabs(false)[0]);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const pageSize = 10;
  const labelsFormatted: string = selectedLabels
    .map((label) => label.id)
    .join(',');
  const isGlobalGuide: boolean = tab === 'Trustpulse';
  const disableArticlesQuery: boolean = tab === 'Utkast';

  const { data: userInfo } = useGetUserinfo();

  const isContentCreator: boolean =
    userInfo?.userRoles?.includes(UserRoles.CONTENT_CREATOR) || false;

  const {
    data: articlesData,
    error: articlesError,
    isLoading: isArticlesLoading,
    isSuccess: isArticlesSuccess,
  } = useGetArticlesQuery({
    pageNumber,
    pageSize,
    sortBy,
    labelIds: labelsFormatted,
    isGlobalGuide,
    disabled: disableArticlesQuery,
  });

  const {
    data: draftArticlesData,
    error: draftArticlesError,
    isLoading: isDraftArticlesLoading,
    isSuccess: isDraftArticlesSuccess,
  } = useGetDraftArticlesQuery({
    pageNumber,
    pageSize,
    sortBy,
    labelIds: labelsFormatted,
    isGlobalGuide: true,
    enabled: tab === 'Utkast' && isContentCreator,
  });

  const articles = articlesData?.articles || [];
  const draftArticles = draftArticlesData?.draftArticles || [];
  const pagination = articlesData?.pagination || { total: 0 };
  const draftPagination = draftArticlesData?.pagination || { total: 0 };

  const totalPages = (paginationType: { total: number }) =>
    Math.ceil(paginationType.total / pageSize);

  useEffect(() => {
    const activePagination = tab === 'Utkast' ? draftPagination : pagination;
    const lastPage = Math.max(1, Math.ceil(activePagination.total / pageSize));

    if (pageNumber > lastPage) {
      setPageNumber(lastPage);
    }
  }, [tab, pagination.total, draftPagination.total, pageSize, pageNumber]);

  return (
    <div className={classes.container}>
      <PageHeadingBanner>
        <PageHeadingBanner.Title>Situasjonshjelp</PageHeadingBanner.Title>
        <PageHeadingBanner.Text>
          Situasjonshjelp gir deg som teamleder verktøy og guider til å
          gjennomføre møter, medarbeidersamtaler, gi tilbakemeldinger – og
          håndtere konflikter og vanskelige situasjoner etter at de har oppstått
          – med trygghet, struktur og inspirasjon. Gjennom konkrete forslag,
          refleksjonsspørsmål og støttemateriell hjelper Situasjonshjelp deg å
          stå støtt i lederrollen, også når samtalene er krevende. Målet er å
          gjøre det lettere å ta ansvar, bygge relasjoner og lede teamet ditt
          med både tydelighet og omtanke.
        </PageHeadingBanner.Text>
      </PageHeadingBanner>

      <TabMenu
        selectedLabels={selectedLabels}
        setSelectedLabels={setSelectedLabels}
        sortBy={sortBy}
        setSortBy={setSortBy}
        tabs={pageTabs(isContentCreator)}
        tab={tab}
        setTab={setTab}
        canCreate={
          userInfo?.userRoles?.includes(UserRoles.CONTENT_CREATOR) || false
        }
      />

      <div className={classes.paddedContent}>
        <div className={classes.articlesContainer}>
          {articlesError && <ErrorContainer error={articlesError} />}
          {draftArticlesError && <ErrorContainer error={draftArticlesError} />}

          {isArticlesLoading && <LoadingContainer title="veiledninger" />}
          {isDraftArticlesLoading && <LoadingContainer title="utkast" />}

          {isArticlesSuccess && !disableArticlesQuery && (
            <ContentContainer
              ternary={
                articles.length > 0
                  ? 'Veiledninger'
                  : 'Ingen veiledninger publisert'
              }
              article={articles}
              type="published"
              isContentCreator={isContentCreator}
            />
          )}

          {isDraftArticlesSuccess && disableArticlesQuery && (
            <ContentContainer
              ternary={
                draftArticles.length > 0 ? 'Utkast' : 'Ingen utkast funnet'
              }
              article={draftArticles}
              type="draft"
              isContentCreator={isContentCreator}
            />
          )}

          {pagination.total > pageSize && tab !== 'Utkast' && (
            <Pagination
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              totalPages={totalPages(pagination)}
            />
          )}
          {draftPagination.total > pageSize && tab === 'Utkast' && (
            <Pagination
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              totalPages={totalPages(draftPagination)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const ErrorContainer = ({ error }: { error: Error }) => {
  return (
    <div className={classes.container}>
      <Alert title={error.name} variant="danger" size="lg">
        <Paragraph size="md">{error.message}</Paragraph>
      </Alert>
    </div>
  );
};

const LoadingContainer = ({ title }: { title: string }) => {
  return (
    <div className={classes.container}>
      <Heading as="h3" className={classes.containerTitle}>
        Laster {title}...
      </Heading>
    </div>
  );
};

const ContentContainer = ({
  ternary,
  article,
  type,
  isContentCreator,
}: {
  ternary: string;
  article: ArticleResponse[];
  type: 'draft' | 'published';
  isContentCreator: boolean;
}) => {
  return (
    <div className={classes.container}>
      <Heading as="h3" className={classes.containerTitle}>
        {ternary}
      </Heading>
      <ArticlesList
        type={type}
        articles={article || []}
        isContentCreator={isContentCreator}
      />
    </div>
  );
};
